/**
 * Table views for CSV import summary rows.
 *
 * - RowCardList: mobile card layout (one card per row)
 * - RowTable: desktop table + mobile card, grouped under a label header
 * - RowTableGroup: renders one RowTable per entity type within a category
 */

import { AlertCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Table, TableCard } from '@/lib/base/table';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CsvImport } from '@/lib/csv-import/api';
import type { CrmObject } from '@/types/crm';

import type {
  ColumnDef,
  ConflictsByRow,
  RowGroup,
} from './RowTableUpdateCells';
import {
  ASSOC_COLUMN_PREFIX,
  ASSOCIATION_COLUMN_TOOLTIP,
  ASSOCIATION_UNRESOLVED_HINT,
  CONFLICT_COLUMN_ID,
  COUNT_COLUMN_ID,
  ERROR_COLUMN_ID,
  RECORD_COLUMN_ID,
  renderRowTableCell,
  REVIEW_CELL_MAX_LENGTH,
} from './RowTableUpdateCells';
import type { RowCategory, RowData } from './types';
import { getPropertyLabel, MAX_TABLE_COLUMNS } from './types';
import { UpdateRowFooter } from './UpdateRowFooter';

export type { ConflictsByRow } from './RowTableUpdateCells';

// ============================================================================
// RowCardList (mobile)
// ============================================================================

interface RowCardListProps {
  rows: RowData[];
  columns: string[];
  schemaMap: PropertySchemaMap;
  newPropertyLabels: Record<string, string>;
  category: RowCategory;
  orgId: number;
  conflictsByRow?: ConflictsByRow;
}

interface AssociationPairsProps {
  associations: NonNullable<RowData['associationDisplay']>;
}

/** Mobile association label:value pairs, rendered below a row's property
 *  pairs and visually separated by a divider (mirrors the error block). */
export function AssociationPairs({ associations }: AssociationPairsProps) {
  return (
    <div className='flex flex-col gap-1.5 mt-1 pt-1.5 border-t border-[var(--color-border-secondary)]'>
      {associations.map(assoc => (
        <div
          key={`${assoc.label}::${assoc.value}`}
          className='flex items-start gap-3'
        >
          <span className='text-xs font-medium text-[var(--color-text-secondary)] w-1/3 shrink-0 truncate'>
            {assoc.label}
          </span>
          <span className='text-xs text-[var(--color-text-primary)] flex-1 break-words'>
            {assoc.value}
            {!assoc.resolved && (
              <span className='text-fg-quaternary'>
                {' '}
                ({ASSOCIATION_UNRESOLVED_HINT})
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Mobile card view — one card per row with label:value pairs. */
function RowCardList({
  rows,
  columns,
  schemaMap,
  newPropertyLabels,
  category,
  orgId,
  conflictsByRow,
}: RowCardListProps) {
  return (
    <div className='divide-y divide-[var(--color-border-secondary)]'>
      {rows.map(row => (
        <div
          key={row.id}
          className='px-4 py-3 space-y-1.5'
          data-testid='row-card'
        >
          {columns.map(col => {
            const value = row.data[col];
            if (value === undefined || value === null || value === '') {
              return null;
            }
            return (
              <div key={col} className='flex items-start gap-3'>
                <span className='text-xs font-medium text-[var(--color-text-secondary)] w-1/3 shrink-0 truncate'>
                  {getPropertyLabel(col, schemaMap, newPropertyLabels)}
                </span>
                <span className='text-xs text-[var(--color-text-primary)] flex-1 break-words'>
                  <PropertyValueLabel
                    value={value}
                    propertyType={schemaMap[col]}
                    fontSize='sm'
                    maxLength={REVIEW_CELL_MAX_LENGTH}
                  />
                </span>
              </div>
            );
          })}
          {row.associationDisplay && row.associationDisplay.length > 0 && (
            <AssociationPairs associations={row.associationDisplay} />
          )}
          {category === 'invalid' && row.error && (
            <div className='flex items-start gap-2 mt-1 pt-1.5 border-t border-[var(--color-border-secondary)]'>
              <AlertCircle
                aria-hidden={true}
                className='w-3.5 h-3.5 text-utility-error-600 flex-shrink-0 mt-0.5'
              />
              <span className='text-xs text-utility-error-700'>
                {row.error}
              </span>
            </div>
          )}
          {/* Mobile counterpart of the desktop "Updates record" / "Conflict"
              columns — narrow screens get the same two facts, stacked. */}
          {category === 'update' && (
            <UpdateRowFooter
              row={row}
              orgId={orgId}
              conflictsByRow={conflictsByRow}
              schemaMap={schemaMap}
              newPropertyLabels={newPropertyLabels}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// RowTable (single entity type table)
// ============================================================================

interface RowTableProps {
  rows: RowData[];
  label: string;
  schemaMap: PropertySchemaMap;
  newPropertyLabels: Record<string, string>;
  category: RowCategory;
  /** Row id (`${fileId}-${row_index}`) that should be scrolled into view
   *  and highlighted. Set when an external trigger (e.g. clicking a row
   *  in the pending-associations table) navigates here. */
  focusedRowId?: string | null;
  /** Entity type the rows in this table belong to. Used to compute a
   *  per-entity dedup key (e.g. ahoy_email for contacts) so identical
   *  records in the CSV collapse into a single visual row. */
  entityTypeName?: string;
  /** Existing CRM records keyed by entity id, for the update category's
   *  "Updates record" column. Undefined while the lookup is still loading. */
  existingObjectsMap?: Record<string, CrmObject>;
  /** Org id, for the record chip's slideout navigation. */
  orgId: number;
  /** Conflicting property names per row id — see ConflictsByRow. */
  conflictsByRow?: ConflictsByRow;
}

/**
 * Per-entity properties used to dedupe rows in the summary tables. Order
 * matters — the first non-empty value wins. Mirrors the unique-property
 * conventions in apps/crm_events/schemas/properties/* (ahoy_email on
 * contacts, ahoy_domain on companies, etc.). Entities without a natural
 * dedup key (notes) are absent from this map — each note row stays its own
 * unique entry.
 */
const DEDUP_CANDIDATES: Record<string, string[]> = {
  ahoy_contact: ['ahoy_email'],
  ahoy_company: ['ahoy_domain', 'ahoy_name'],
  ahoy_deal: ['ahoy_name'],
};

/** Compute the dedup key for a row, or null when no natural key exists. */
function getRowDedupKey(
  row: RowData,
  entityTypeName: string | undefined
): string | null {
  if (!entityTypeName) {
    return null;
  }
  const candidates = DEDUP_CANDIDATES[entityTypeName];
  if (!candidates) {
    return null;
  }
  for (const prop of candidates) {
    const raw = row.data[prop];
    if (raw === undefined || raw === null) {
      continue;
    }
    const value = String(raw).trim().toLowerCase();
    if (value !== '') {
      return `${entityTypeName}::${prop}::${value}`;
    }
  }
  return null;
}

/**
 * Properties used to identify each entity type in compact table views (e.g.
 * the invalid-rows table where we only need to show the entity name plus the
 * field that failed validation). Order matters — earlier properties take
 * priority if multiple are present in the row data.
 */
const DISPLAY_NAME_PROPERTIES: Record<string, string[]> = {
  ahoy_contact: ['ahoy_first_name', 'ahoy_last_name', 'ahoy_email'],
  ahoy_company: ['ahoy_name', 'ahoy_domain'],
  ahoy_deal: ['ahoy_name'],
  ahoy_note: ['ahoy_title'],
};

const INITIAL_ROW_CAP = 25;
const ROW_CAP_INCREMENT = 100;

export function RowTable({
  rows,
  label,
  schemaMap,
  newPropertyLabels,
  category,
  focusedRowId,
  entityTypeName,
  existingObjectsMap,
  orgId,
  conflictsByRow,
}: RowTableProps) {
  // Group rows by entity dedup key (ahoy_email for contacts, etc.). Rows
  // without a usable dedup key remain ungrouped (each is its own group keyed
  // by row.id). The sample row's data is what the table actually renders.
  const rowGroups = useMemo<RowGroup[]>(() => {
    const byKey = new Map<string, RowGroup>();
    const ungrouped: RowGroup[] = [];
    for (const row of rows) {
      const dedupKey = getRowDedupKey(row, entityTypeName);
      if (!dedupKey) {
        ungrouped.push({
          key: row.id,
          sample: row,
          memberRowIds: [row.id],
          memberRowNumbers: [row.row_index + 1],
        });
        continue;
      }
      const existing = byKey.get(dedupKey);
      if (existing) {
        existing.memberRowIds.push(row.id);
        existing.memberRowNumbers.push(row.row_index + 1);
      } else {
        byKey.set(dedupKey, {
          key: dedupKey,
          sample: row,
          memberRowIds: [row.id],
          memberRowNumbers: [row.row_index + 1],
        });
      }
    }
    const groups = [...byKey.values(), ...ungrouped];
    for (const g of groups) {
      g.memberRowNumbers.sort((a, b) => a - b);
    }
    return groups;
  }, [rows, entityTypeName]);

  // Locate the group containing the focused row plus its index in
  // rowGroups. The index lets progressive disclosure extend the visible
  // window when navigation targets a row beyond the current cap.
  const focusedGroup = useMemo<{
    sampleId: string;
    index: number;
  } | null>(() => {
    if (!focusedRowId) {
      return null;
    }
    for (let i = 0; i < rowGroups.length; i++) {
      if (rowGroups[i].memberRowIds.includes(focusedRowId)) {
        return { sampleId: rowGroups[i].sample.id, index: i };
      }
    }
    return null;
  }, [rowGroups, focusedRowId]);
  const focusedGroupSampleId = focusedGroup?.sampleId ?? null;

  // Progressive disclosure: show a limited number of row groups initially
  // so large imports (thousands of rows) don't freeze the browser when
  // an accordion opens. When navigation targets a row beyond the cap,
  // extend the visible window to include it — otherwise the data-row-id
  // lookup below has no DOM node to scroll to and focus is silently lost.
  const [displayCap, setDisplayCap] = useState(INITIAL_ROW_CAP);
  const effectiveCap =
    focusedGroup && focusedGroup.index >= displayCap
      ? focusedGroup.index + 1
      : displayCap;
  const isCapped = rowGroups.length > effectiveCap;
  const visibleGroups = useMemo(
    () => (isCapped ? rowGroups.slice(0, effectiveCap) : rowGroups),
    [rowGroups, effectiveCap, isCapped]
  );
  // Mobile card list caps by raw row count (not group count) because the
  // dedup ratio means 25 groups ≠ 25 raw rows. The two views are mutually
  // exclusive via sm: breakpoints, so sharing displayCap is safe.
  const mobileIsCapped = rows.length > displayCap;
  const handleShowMore = useCallback(() => {
    setDisplayCap(prev => Math.max(prev, effectiveCap) + ROW_CAP_INCREMENT);
  }, [effectiveCap]);

  // Scroll the focused row into view when it lands in this table. We use a
  // hidden marker selector because react-aria-components' Table.Row doesn't
  // forward refs to the underlying <tr>. requestAnimationFrame defers the
  // lookup so it runs after the accordion finishes mounting the panel.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focusedGroupSampleId || !containerRef.current) {
      return undefined;
    }
    const handle = requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector(
        `[data-row-id="${CSS.escape(focusedGroupSampleId)}"]`
      );
      if (el && 'scrollIntoView' in el) {
        (el as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    });
    return () => cancelAnimationFrame(handle);
  }, [focusedGroupSampleId]);

  const columns = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    // Derive columns from ALL group samples (not just visible ones) so the
    // column set stays stable when the user clicks "Show more".
    for (const group of rowGroups) {
      for (const key of Object.keys(group.sample.data || {})) {
        if (!seen.has(key)) {
          seen.add(key);
          result.push(key);
        }
      }
    }
    const allColumns = result.slice(0, MAX_TABLE_COLUMNS);

    // For invalid rows, show only the display-name column(s) and the
    // properties that caused errors — the full column set is overwhelming
    // when users just need to see which entity failed and why.
    if (category === 'invalid' && entityTypeName) {
      const displayProps = new Set(
        DISPLAY_NAME_PROPERTIES[entityTypeName] ?? []
      );
      const errorProps = new Set<string>();
      for (const group of rowGroups) {
        for (const prop of group.sample.errorProperties ?? []) {
          errorProps.add(prop);
        }
      }
      const relevantCols = allColumns.filter(
        col => displayProps.has(col) || errorProps.has(col)
      );
      if (relevantCols.length > 0) {
        return relevantCols;
      }
    }

    return allColumns;
  }, [rowGroups, category, entityTypeName]);

  // Ordered, deduped association labels across all group samples (mirrors
  // the column-dedup pattern above). Rows without any resolved associations
  // (plain CSV imports) yield an empty list, so no association columns
  // render for them.
  const associationLabels = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const group of rowGroups) {
      for (const assoc of group.sample.associationDisplay ?? []) {
        if (!seen.has(assoc.label)) {
          seen.add(assoc.label);
          result.push(assoc.label);
        }
      }
    }
    return result;
  }, [rowGroups]);

  // Column defs for the library Table's dynamic-items API. Association
  // columns (when present) sit right after the first (identifying) column:
  // they are the consent-relevant information this review exists to surface,
  // and appending them after every property column let a single long text
  // property push them off-screen — invisible without horizontal scrolling,
  // which is exactly the blindness the feature exists to fix. Then the
  // remaining property columns, an Error column when this category renders
  // failures, and a Count column at the end (so duplicates are visible).
  //
  // Count only means something once two or more source rows have collapsed into
  // one record. When every group is a single row — always true for a
  // source-driven import, where each record is already unique — the column is a
  // wall of "1"s, so leave it out entirely rather than showing noise.
  const columnDefs = useMemo<ColumnDef[]>(() => {
    const defs: ColumnDef[] = columns.map(col => ({
      id: col,
      label: getPropertyLabel(col, schemaMap, newPropertyLabels),
    }));
    const assocDefs: ColumnDef[] = associationLabels.map(label => ({
      id: ASSOC_COLUMN_PREFIX + label,
      label,
    }));
    // After the identifying first column; degenerate case of no property
    // columns appends them at the start.
    defs.splice(Math.min(1, defs.length), 0, ...assocDefs);
    if (category === 'invalid') {
      defs.push({ id: ERROR_COLUMN_ID, label: 'Error' });
    }
    // Updates change a record that already exists, so the table has to say
    // which one — that identification is the whole reason this category used to
    // render as accordions instead of a table.
    if (category === 'update') {
      defs.push({ id: RECORD_COLUMN_ID, label: 'Updates record' });
      // Only when something actually conflicts, so a clean import isn't given a
      // column of blanks (same rule as Count below).
      if (rowGroups.some(group => group.sample.hasConflict)) {
        defs.push({ id: CONFLICT_COLUMN_ID, label: 'Conflict' });
      }
    }
    if (rowGroups.some(group => group.memberRowIds.length > 1)) {
      defs.push({ id: COUNT_COLUMN_ID, label: 'Count' });
    }
    return defs;
  }, [
    columns,
    schemaMap,
    newPropertyLabels,
    category,
    rowGroups,
    associationLabels,
  ]);

  return (
    <div className='space-y-2' ref={containerRef}>
      {/* Section heading rendered ABOVE the bordered table card so it can't
          visually merge with the Table.Header's grey row. */}
      <div className='flex items-baseline justify-between gap-3 px-1'>
        <h4 className='text-sm font-semibold text-[var(--color-text-primary)]'>
          {label}
        </h4>
        <span className='text-xs text-[var(--color-text-tertiary)] tabular-nums'>
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
        </span>
      </div>

      {/* Bordered table card. Owns its own horizontal scroll so wide tables
          (many columns / long error messages) scroll inside the card rather
          than overflowing the page or getting truncated. */}
      <div className='border border-[var(--color-border-secondary)] rounded-lg overflow-hidden'>
        {/* Desktop: library Table with size='sm'. Sticky header, row hover,
            and bottom borders come from Table.* itself. TableCard.ScrollArea
            owns horizontal scroll + the edge-fade hint so the user knows
            there's more table off-screen. */}
        <div className='hidden sm:block'>
          <TableCard.ScrollArea>
            <Table size='sm' aria-label={label}>
              {/* Use the dynamic-items API: pass `columns` + a function child so
              react-aria-components registers each column in the collection.
              Without `columns`, dynamic .map() output doesn't get tracked and
              the runtime errors with "Cell count must match column count". */}
              <Table.Header columns={columnDefs}>
                {column => (
                  // react-aria-components requires at least one column to be
                  // marked as the row header (used to label the row to assistive
                  // tech). The first data column is the natural fit — columns
                  // are dynamic per file, so we anchor on index 0 of columnDefs.
                  <Table.Head
                    id={column.id}
                    label={column.label}
                    isRowHeader={column.id === columnDefs[0]?.id}
                    tooltip={
                      column.id.startsWith(ASSOC_COLUMN_PREFIX)
                        ? ASSOCIATION_COLUMN_TOOLTIP
                        : undefined
                    }
                  />
                )}
              </Table.Header>
              <Table.Body items={visibleGroups}>
                {group => (
                  <Table.Row
                    id={group.key}
                    columns={columnDefs}
                    // data-row-id targets the SAMPLE row id so the scroll-into-
                    // view lookup from a focused-row-id click works (the sample
                    // is what the table actually renders).
                    data-row-id={group.sample.id}
                    className={
                      group.sample.id === focusedGroupSampleId
                        ? 'bg-utility-warning-50 ring-2 ring-inset ring-utility-warning-300'
                        : undefined
                    }
                  >
                    {column =>
                      renderRowTableCell({
                        column,
                        group,
                        category,
                        schemaMap,
                        newPropertyLabels,
                        orgId,
                        conflictsByRow,
                        existingObjectsMap,
                      })
                    }
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </TableCard.ScrollArea>
        </div>

        {/* Mobile: stacked card view. Caps by raw row count (independent of
            dedup group count) so large imports don't freeze the browser.
            Hidden on desktop so the extra card DOM is never in the live tree
            when the desktop table is visible. */}
        <div className='sm:hidden'>
          <RowCardList
            rows={rows.slice(0, displayCap)}
            columns={columns}
            schemaMap={schemaMap}
            newPropertyLabels={newPropertyLabels}
            category={category}
            orgId={orgId}
            conflictsByRow={conflictsByRow}
          />
          {mobileIsCapped && (
            <div className='flex justify-center py-2 border-t border-[var(--color-border-secondary)]'>
              <Button color='link-gray' size='sm' onClick={handleShowMore}>
                Show more ({rows.length - displayCap} remaining of {rows.length}
                )
              </Button>
            </div>
          )}
        </div>

        {isCapped && (
          <div className='hidden sm:flex justify-center py-2 border-t border-[var(--color-border-secondary)]'>
            <Button color='link-gray' size='sm' onClick={handleShowMore}>
              Show more ({rowGroups.length - effectiveCap} remaining of{' '}
              {rowGroups.length})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RowTableGroup (groups tables by entity type)
// ============================================================================

interface RowTableGroupProps {
  rows: RowData[];
  importData: CsvImport;
  schemaMap: PropertySchemaMap;
  /** Per-entity-type schema maps (see useEntitySchemaMap). When provided, each
   *  entity type's table resolves property labels from its own type's schema,
   *  so cross-entity name collisions (`ahoy_name` → "Company Name" vs "Deal
   *  Name") cannot mislabel a column. Falls back to the flat `schemaMap` for
   *  types missing from the record (e.g. schema still loading). */
  schemaMapByEntityType?: Record<string, PropertySchemaMap>;
  newPropertyLabels: Record<string, string>;
  category: RowCategory;
  getEntityTypeLabel: (name: string) => string;
  /** Row id passed through to whichever per-entity RowTable contains it. */
  focusedRowId?: string | null;
  /** Existing CRM records keyed by entity id — see RowTableProps. */
  existingObjectsMap?: Record<string, CrmObject>;
  /** Org id, for the record chip's slideout navigation. */
  orgId: number;
  /** Conflicting property names per row id — see ConflictsByRow. */
  conflictsByRow?: ConflictsByRow;
}

/** Renders one table per entity type within a category. */
export function RowTableGroup({
  rows,
  importData,
  schemaMap,
  schemaMapByEntityType,
  newPropertyLabels,
  category,
  getEntityTypeLabel,
  focusedRowId,
  existingObjectsMap,
  orgId,
  conflictsByRow,
}: RowTableGroupProps) {
  // Build file_id → entity_type_name lookup
  const fileToEntityType = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const file of importData.uploaded_files) {
      lookup[String(file.file_id)] = file.entity_type_name;
    }
    return lookup;
  }, [importData.uploaded_files]);

  // Group rows by entity type
  const groupedRows = useMemo(() => {
    const groups: Record<string, RowData[]> = {};
    for (const row of rows) {
      const entityType = fileToEntityType[row.file_id] ?? 'unknown';
      if (!groups[entityType]) {
        groups[entityType] = [];
      }
      groups[entityType].push(row);
    }
    return groups;
  }, [rows, fileToEntityType]);

  const entityTypes = Object.keys(groupedRows);

  // RowTable now owns its own heading + bordered card, so this wrapper only
  // needs to provide spacing between tables.
  return (
    <div className='p-3 space-y-4'>
      {entityTypes.map(entityType => (
        <RowTable
          key={entityType}
          rows={groupedRows[entityType]}
          label={getEntityTypeLabel(entityType)}
          schemaMap={schemaMapByEntityType?.[entityType] ?? schemaMap}
          newPropertyLabels={newPropertyLabels}
          category={category}
          focusedRowId={focusedRowId}
          entityTypeName={entityType}
          existingObjectsMap={existingObjectsMap}
          orgId={orgId}
          conflictsByRow={conflictsByRow}
        />
      ))}
    </div>
  );
}
